// Request Queue Service for Live Monitoring
// Manages and batches database requests to prevent connection overload

class RequestQueue {
  constructor() {
    this.queue = [];
    this.isProcessing = false;
    this.batchSize = 10; // Process up to 10 requests at once
    this.batchTimeout = 5000; // Wait up to 5 seconds to batch requests
    this.processingInterval = null;
    this.lastProcessTime = 0;
    this.maxRetries = 3;
    this.retryDelay = 2000; // 2 seconds between retries
  }

  // Add a request to the queue
  addRequest(request) {
    const queueItem = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      request: request,
      timestamp: Date.now(),
      retries: 0,
      priority: request.priority || 'normal' // 'high', 'normal', 'low'
    };

    this.queue.push(queueItem);
    // console.log(`📥 Request queued: ${request.type} (Queue size: ${this.queue.length})`);

    // Start processing if not already running
    this.startProcessing();

    return queueItem.id;
  }

  // Start the processing loop
  startProcessing() {
    if (this.processingInterval) return;

    this.processingInterval = setInterval(() => {
      this.processQueue();
    }, 1000); // Check every second

    // console.log('🔄 Request queue processing started');
  }

  // Stop the processing loop
  stopProcessing() {
    if (this.processingInterval) {
      clearInterval(this.processingInterval);
      this.processingInterval = null;
      // console.log('⏹️ Request queue processing stopped');
    }
  }

  // Process the queue
  async processQueue() {
    if (this.isProcessing || this.queue.length === 0) return;

    // Don't process too frequently
    const now = Date.now();
    if (now - this.lastProcessTime < 1000) return; // Minimum 1 second between batches

    this.isProcessing = true;
    this.lastProcessTime = now;

    try {
      // Sort by priority and timestamp
      this.queue.sort((a, b) => {
        const priorityOrder = { high: 3, normal: 2, low: 1 };
        const aPriority = priorityOrder[a.priority] || 2;
        const bPriority = priorityOrder[b.priority] || 2;
        
        if (aPriority !== bPriority) {
          return bPriority - aPriority; // Higher priority first
        }
        
        return a.timestamp - b.timestamp; // Older requests first
      });

      // Take a batch of requests
      const batch = this.queue.splice(0, this.batchSize);
      
      // console.log(`📦 Processing batch of ${batch.length} requests`);

      // Process the batch
      await this.processBatch(batch);

    } catch (error) {
      console.error('❌ Error processing request queue:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  // Process a batch of requests
  async processBatch(batch) {
    const results = [];

    for (const item of batch) {
      try {
        // console.log(`📤 Processing request: ${item.request.type} (ID: ${item.id})`);
        
        const result = await this.executeRequest(item.request);
        results.push({ id: item.id, success: true, result });

      } catch (error) {
        console.warn(`⚠️ Request failed: ${item.request.type} (ID: ${item.id})`, error);
        
        // Retry logic
        if (item.retries < this.maxRetries) {
          item.retries++;
          item.timestamp = Date.now(); // Update timestamp for retry
          
          // Add back to queue with delay
          setTimeout(() => {
            this.queue.push(item);
            // console.log(`🔄 Retrying request: ${item.request.type} (Attempt ${item.retries}/${this.maxRetries})`);
          }, this.retryDelay * item.retries);
          
        } else {
          console.error(`❌ Request failed permanently: ${item.request.type} (ID: ${item.id})`);
          results.push({ id: item.id, success: false, error: error.message });
        }
      }
    }

    return results;
  }

  // Execute a single request
  async executeRequest(request) {
    const { type, endpoint, data } = request;

    // Check if this is a cross-origin request
    const isCrossOrigin = !endpoint.startsWith(window.location.origin) && !endpoint.startsWith('/');
    
    const headers = {
      'Content-Type': 'application/json'
    };

    // Only add Authorization header for same-origin requests or if we have a token
    const authToken = localStorage.getItem('authToken');
    if (authToken && !isCrossOrigin) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    // Add CORS headers for cross-origin requests
    if (isCrossOrigin) {
      headers['Origin'] = window.location.origin;
    }

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(data),
      signal: AbortSignal.timeout(10000), // 10 second timeout
      mode: isCrossOrigin ? 'cors' : 'same-origin',
      credentials: isCrossOrigin ? 'omit' : 'same-origin'
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  }

  // Get queue status
  getStatus() {
    return {
      queueSize: this.queue.length,
      isProcessing: this.isProcessing,
      lastProcessTime: this.lastProcessTime,
      processingInterval: !!this.processingInterval
    };
  }

  // Clear the queue
  clear() {
    this.queue = [];
    // console.log('🧹 Request queue cleared');
  }

  // Get queue statistics
  getStats() {
    const now = Date.now();
    const priorities = this.queue.reduce((acc, item) => {
      acc[item.priority] = (acc[item.priority] || 0) + 1;
      return acc;
    }, {});

    return {
      totalRequests: this.queue.length,
      priorities,
      oldestRequest: this.queue.length > 0 ? now - Math.min(...this.queue.map(q => q.timestamp)) : 0,
      averageAge: this.queue.length > 0 ? 
        this.queue.reduce((sum, q) => sum + (now - q.timestamp), 0) / this.queue.length : 0
    };
  }
}

// Create singleton instance
const requestQueue = new RequestQueue();

// Export for use in other services
export default requestQueue;

// Also attach to window for debugging
if (typeof window !== 'undefined') {
  window.requestQueue = requestQueue;
} 